import { useState } from "react";
import { Loader2, User, GraduationCap, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-border bg-card p-6">
    <div className="mb-5 flex items-center gap-2">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></span>
      <h2 className="font-heading text-base font-semibold">{title}</h2>
    </div>
    {children}
  </div>
);

export default function Settings() {
  const { user, setUser } = useAuth();
  const [exam, setExam] = useState(user?.examPreference || "JEE");
  const [level, setLevel] = useState(user?.jeeLevel || "MAIN");
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => thisYear + i);
  const [year, setYear] = useState(String(user?.examYear || thisYear));
  const [savingPref, setSavingPref] = useState(false);

  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [digest, setDigest] = useState(user?.weeklyDigestEnabled !== false);
  const toggleDigest = async (val) => {
    setDigest(val);
    try {
      const { data } = await api.put("/student/email-prefs", { weeklyDigestEnabled: val });
      setUser(data);
      toast.success(val ? "Weekly emails on" : "Weekly emails off");
    } catch (e) {
      setDigest(!val);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePref = async () => {
    setSavingPref(true);
    try {
      const { data } = await api.put("/auth/onboarding", { examPreference: exam, jeeLevel: exam === "JEE" ? level : null, examYear: Number(year) });
      setUser(data);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPref(false);
    }
  };

  const savePw = async () => {
    setSavingPw(true);
    try {
      await api.put("/auth/password", { currentPassword: cur, newPassword: next });
      toast.success("Password updated");
      setCur(""); setNext("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="settings-page">
      <Card icon={User} title="Profile">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Name</Label><Input value={user?.name || ""} disabled className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={user?.email || ""} disabled className="rounded-xl" /></div>
        </div>
      </Card>

      <Card icon={GraduationCap} title="Exam Preference">
        <div className="flex gap-3">
          {["JEE", "NEET"].map((e) => (
            <button key={e} onClick={() => setExam(e)} data-testid={`pref-${e}`}
              className={cn("flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-colors", exam === e ? "border-blue-600 bg-blue-600 text-white" : "border-border text-muted-foreground hover:border-blue-300")}>
              {e}
            </button>
          ))}
        </div>
        {exam === "JEE" && (
          <div className="mt-3 flex gap-3">
            {["MAIN", "ADVANCED"].map((l) => (
              <button key={l} onClick={() => setLevel(l)} data-testid={`level-${l}`}
                className={cn("flex-1 rounded-xl border px-4 py-2.5 text-sm transition-colors", level === l ? "border-blue-600 bg-blue-50 text-blue-700" : "border-border text-muted-foreground")}>
                {l === "MAIN" ? "JEE Main" : "JEE Advanced"}
              </button>
            ))}
          </div>
        )}
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium">Target exam year</p>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {years.map((y) => (
              <button key={y} onClick={() => setYear(String(y))} data-testid={`year-${y}`}
                className={cn("rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors", year === String(y) ? "border-blue-600 bg-blue-600 text-white" : "border-border text-muted-foreground hover:border-blue-300")}>
                {y}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={savePref} disabled={savingPref} data-testid="save-pref-btn" className="mt-5 rounded-xl bg-blue-600 hover:bg-blue-700">
          {savingPref && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save preferences
        </Button>
      </Card>

      <Card icon={Mail} title="Email Notifications">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-foreground">Weekly progress email</div>
            <p className="mt-0.5 text-sm text-muted-foreground">A Friday summary of your performance, weak chapters and leaderboard rank.</p>
          </div>
          <Switch checked={digest} onCheckedChange={toggleDigest} data-testid="digest-toggle" />
        </div>
      </Card>

      <Card icon={KeyRound} title="Change Password">
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-pw" className="rounded-xl" placeholder="Leave blank if you signed up with Google" /></div>
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-pw" className="rounded-xl" placeholder="At least 6 characters" /></div>
        </div>
        <Button onClick={savePw} disabled={savingPw || next.length < 6} data-testid="save-pw-btn" className="mt-5 rounded-xl bg-blue-600 hover:bg-blue-700">
          {savingPw && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
        </Button>
      </Card>
    </div>
  );
}
